import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { TextureLoader, Vector3, DoubleSide, RGBAFormat } from 'three';
import * as THREE from 'three';
import { generatePositions } from './helpers';

const PlaneSpawner = forwardRef(({
    textureUrl,
    roughness = 1,
    metalness = 1,
    minSpawnPositionX,
    minSpawnPositionY,
    minSpawnPositionZ,
    maxSpawnPositionX,
    maxSpawnPositionY,
    maxSpawnPositionZ,
    minDistanceBetweenObjects = 0.5,
    minRotation,
    maxRotation,
    minObjects = 5,
    maxObjects = 10,
    randomizer = false, // Legacy support
    randomizePosition = false,
    randomizeRotation = false,
    isExporting = false, // Wird während Export gesetzt
    scale = 1.0 // Skalierungsfaktor wie bei ModelSpawner
}, ref) => {
    const [planes, setPlanes] = useState([]);
    const [textureCache, setTextureCache] = useState({});
    const [texture, setTexture] = useState(null);
    const [textureAspectRatio, setTextureAspectRatio] = useState(1.0); // Seitenverhältnis der Textur
    const { gl: renderer } = useThree();
    const meshRefs = useRef([]);
    
    // Exponiere Mesh-Referenzen für Export
    useImperativeHandle(ref, () => ({
        getObjects: () => meshRefs.current.filter(ref => ref !== null),
        getObjectCount: () => meshRefs.current.filter(ref => ref !== null).length
    }));

    // Lade Textur und ermittle Bildgröße für Seitenverhältnis
    useEffect(() => {
        if (!textureUrl) {
            console.log('PlaneSpawner: No textureUrl provided');
            return;
        }
        
        console.log('PlaneSpawner: Loading texture:', textureUrl);
        
        if (textureCache[textureUrl]) {
            const cachedTexture = textureCache[textureUrl];
            setTexture(cachedTexture);
            // Berechne Seitenverhältnis aus geladener Textur
            if (cachedTexture.image) {
                const aspect = cachedTexture.image.width / cachedTexture.image.height;
                console.log('PlaneSpawner: Using cached texture, aspect ratio:', aspect);
                setTextureAspectRatio(aspect);
            }
        } else {
            const loader = new TextureLoader();
            loader.load(
                textureUrl,
                (loadedTexture) => {
                    console.log('PlaneSpawner: Texture loaded successfully');
                    loadedTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
                    
                    // Stelle sicher, dass Alpha-Kanal korrekt behandelt wird
                    if (loadedTexture.format !== RGBAFormat) {
                        // Prüfe ob das Bild einen Alpha-Kanal hat
                        const hasAlpha = loadedTexture.image && (
                            loadedTexture.image.width > 0 && loadedTexture.image.height > 0 &&
                            (loadedTexture.image.src?.includes('.png') || loadedTexture.image.src?.includes('data:image/png'))
                        );
                        if (hasAlpha) {
                            loadedTexture.format = RGBAFormat;
                        }
                    }
                    loadedTexture.premultiplyAlpha = false;
                    
                    // Berechne Seitenverhältnis aus Bildgröße
                    let aspect = 1.0;
                    if (loadedTexture.image) {
                        aspect = loadedTexture.image.width / loadedTexture.image.height;
                        console.log('PlaneSpawner: Image dimensions:', loadedTexture.image.width, 'x', loadedTexture.image.height, 'aspect:', aspect);
                    } else {
                        console.warn('PlaneSpawner: Texture loaded but image is null');
                    }
                    setTextureAspectRatio(aspect);
                    
                    setTextureCache({
                        ...textureCache,
                        [textureUrl]: loadedTexture
                    });
                    setTexture(loadedTexture);
                },
                undefined,
                (error) => {
                    console.error('PlaneSpawner: Failed to load texture:', textureUrl, error);
                }
            );
        }
    }, [textureUrl, textureCache, renderer]);

    useEffect(() => {
        if (!textureUrl) {
            console.log('PlaneSpawner: No textureUrl provided');
            setPlanes([]);
            meshRefs.current = [];
            return;
        }

        const objectCount = Math.floor(Math.random() * (maxObjects - minObjects + 1)) + minObjects;
        console.log('PlaneSpawner: Spawning', objectCount, 'planes');
        
        const positions = generatePositions(
            objectCount,
            new Vector3(minSpawnPositionX, minSpawnPositionY, minSpawnPositionZ),
            new Vector3(maxSpawnPositionX, maxSpawnPositionY, maxSpawnPositionZ),
            minDistanceBetweenObjects
        );

        const newPlanes = positions
            .filter(pos => pos !== null && pos !== undefined)
            .map((pos) => ({
                position: pos instanceof Vector3 ? pos : new Vector3(pos.x, pos.y, pos.z),
                rotation: [
                    Math.random() * (maxRotation - minRotation) + minRotation,
                    Math.random() * (maxRotation - minRotation) + minRotation,
                    Math.random() * (maxRotation - minRotation) + minRotation
                ]
            }));

        console.log('PlaneSpawner: Created', newPlanes.length, 'planes at positions:', newPlanes.map(p => p.position));
        setPlanes(newPlanes);
        // Initialisiere meshRefs Array
        meshRefs.current = new Array(newPlanes.length).fill(null);
    }, [minObjects, maxObjects, minSpawnPositionX, minSpawnPositionY, minSpawnPositionZ, maxSpawnPositionX, maxSpawnPositionY, maxSpawnPositionZ, minDistanceBetweenObjects, minRotation, maxRotation, textureUrl]);

    useFrame(() => {
        // Randomizer nur während Export aktivieren
        const shouldRandomize = isExporting && (randomizer || randomizePosition || randomizeRotation);
        
        if (shouldRandomize) {
            let newPositions = null;
            
            // Randomisiere Position wenn aktiviert
            if (randomizer || randomizePosition) {
                newPositions = generatePositions(
                    planes.length,
                    new Vector3(minSpawnPositionX, minSpawnPositionY, minSpawnPositionZ),
                    new Vector3(maxSpawnPositionX, maxSpawnPositionY, maxSpawnPositionZ),
                    minDistanceBetweenObjects
                );
            }

            setPlanes((currentPlanes) =>
                currentPlanes.map((plane, i) => {
                    const newPos = newPositions && newPositions[i] ? newPositions[i] : plane.position;
                    const shouldRotate = randomizer || randomizeRotation;
                    
                    return {
                        ...plane,
                        position: newPos,
                        rotation: shouldRotate ? [
                            Math.random() * (maxRotation - minRotation) + minRotation,
                            Math.random() * (maxRotation - minRotation) + minRotation,
                            Math.random() * (maxRotation - minRotation) + minRotation
                        ] : plane.rotation
                    };
                })
            );
        }
    });

    // Berechne Plane-Größe basierend auf Seitenverhältnis und Skalierung
    // Genau wie ModelSpawner: Zielgröße 2.0 Einheiten (TARGET_BOUNDING_BOX_SIZE)
    const TARGET_BOUNDING_BOX_SIZE = 2.0;
    const baseSize = TARGET_BOUNDING_BOX_SIZE * scale;
    
    // Plane-Größe: Breite basierend auf Seitenverhältnis
    // Wenn aspect > 1: Breite ist größer, Höhe wird angepasst
    // Wenn aspect < 1: Höhe ist größer, Breite wird angepasst
    // Die größte Dimension soll genau baseSize sein
    let planeWidth, planeHeight;
    if (textureAspectRatio >= 1.0) {
        // Querformat oder quadratisch: Breite ist die Basis
        planeWidth = baseSize;
        planeHeight = baseSize / textureAspectRatio;
    } else {
        // Hochformat: Höhe ist die Basis
        planeHeight = baseSize;
        planeWidth = baseSize * textureAspectRatio;
    }

    console.log('PlaneSpawner: Rendering', planes.length, 'planes, size:', planeWidth, 'x', planeHeight, 'texture:', !!texture);

    if (!texture) {
        console.log('PlaneSpawner: No texture loaded yet, waiting...');
        return null;
    }

    if (planes.length === 0) {
        console.log('PlaneSpawner: No planes to render');
        return null;
    }

    return (
        <>
            {planes.map((plane, i) => {
                if (!plane || !plane.position) return null;
                
                // Stelle sicher, dass position ein Vector3 ist
                const pos = plane.position instanceof Vector3 
                    ? plane.position 
                    : new Vector3(plane.position.x || 0, plane.position.y || 0, plane.position.z || 0);
                
                    return (
                    <mesh 
                        key={i} 
                        ref={el => meshRefs.current[i] = el}
                        position={pos.toArray()} 
                        rotation={plane.rotation || [0, 0, 0]}
                    >
                        <planeGeometry args={[planeWidth, planeHeight]} />
                        <meshStandardMaterial
                            map={texture}
                            side={DoubleSide}
                            roughness={roughness}
                            metalness={metalness}
                            transparent={true}
                            opacity={1}
                            alphaTest={0.01}
                        />
                    </mesh>
                );
            })}
        </>
    );
});

PlaneSpawner.displayName = 'PlaneSpawner';

export default PlaneSpawner;
